<template src="./Slider.html"></template>
<style src="./Slider.scss" lang="scss"></style>

<script>
  import Image from '@/components/Image/Image'
  import Teaser from '@/components/Teaser/Teaser'
  import Icon from '@/components/Icon/Icon'

  /**
   * Constants for slide direction and animation type of slider.
   * @type {{righttoleft: string, bottomtotop: string, fade: string, toptobottom: string, lefttoright: string}}
   */
  export const ANIMATION_TYPE = {
    lefttoright: 'lefttoright',
    righttoleft: 'righttoleft',
    toptobottom: 'toptobottom',
    bottomtotop: 'bottomtotop',
    fade: 'fade'
  }

  /**
   * Constants for position of indicators for slider.
   * @type {{top: string, left: string, bottom: string, right: string}}
   */
  export const INDICATOR_POSITIONS = {
    top: 'top',
    bottom: 'bottom',
    left: 'left',
    right: 'right'
  }

  /**
   * A container component to hold an unspecified number of elements with element change animation.
   * @vue-data {number} current The currently visible slide
   * @vue-data {Element} slideTrack The container containing all the slides
   * @vue-data {Element[]} slides An array containing all the slides
   * @vue-data {Element[]} indicators An array containing all the indicators
   * @vue-data {number} autoslide The internal reference for slide interval
   * @vue-data {Object} touchDetector An object containing details about touch detection
   * @vue-prop {Array} elements An array of elements to be slided
   * @vue-prop {'' | 'lefttoright' | 'righttoleft' | 'toptobottom' | 'bottomtotop' | 'fade'} [animationType] The axis and direction the slider should slide
   * @vue-prop {'' | 'top' | 'bottom' | 'left' | 'right'} [indicatorPosition] Position of the indicators
   * @vue-prop {number} [interval] The interval in which the slider should auto slide
   * @vue-prop {boolean} [showArrows] If the arrows for slider should be visible
   * @vue-prop {boolean} [showIndicators] If the indicators for slider should be visible
   * @listens Document#gridGapChanged
   * @listens Document#colCountChanged
   */
  export default {
    data () {
      return {
        current: 0,
        slideTrack: null,
        slides: [],
        indicators: [],
        autoslide: '',
        indicatorsData: [],
        touchDetector: {
          startX: '',
          startY: '',
          endX: '',
          endY: ''
        }
      }
    },
    props: {
      elements: Array,
      animationType: {
        type: String,
        required: false,
        default: ANIMATION_TYPE.lefttoright
      },
      indicatorPosition: {
        type: String,
        required: false,
        default: INDICATOR_POSITIONS.top
      },
      interval: {
        type: Number,
        required: false,
        default: 0
      },
      showArrows: {
        type: Boolean,
        required: false,
        default: true
      },
      showIndicators: {
        type: Boolean,
        required: false,
        default: true
      }
    },
    components: {
      'hp-image': Image,
      'hp-teaser': Teaser,
      'hp-icon': Icon
    },
    methods: {
      /**
       * Selects the previous slide.
       * Wraps around to last slide if currently on first slide.
       */
      goPrev () {
        this.current === 0 ? (this.current = this.slides.length - 1) : this.current--
        this.moveContentWrapper()
      },
      /**
       * Selects the next slide.
       * Wraps around to first slide if currently on last slide.
       */
      goNext () {
        this.current === this.slides.length - 1 ? (this.current = 0) : this.current++
        this.moveContentWrapper()
      },
      /**
       * Selects a slide. Usually fired by indicators.
       * @param {Event} event Clickevent which is fired on indicator click.
       */
      goTo (event) {
        this.current = event.currentTarget.dataset.indicator !== undefined ? +event.currentTarget.dataset.indicator : +event.currentTarget.parentElement.dataset.indicator
        this.moveContentWrapper()
      },
      /**
       * Moves the indicator bar to the right on click to show currently invisible indicators.
       * @param {Element} element The clicked indicator element
       * @param {Element} container The container holding clicked element and to be moved
       * @param {Element} reference The reference container in which the container element should be moved
       */
      rulerRightHandler (element, container, reference) {
        const ruler = Math.abs(container.offsetLeft) + reference.offsetWidth
        const offsetRight = element.offsetLeft + element.offsetWidth - 1 // remove the overlapped 1px
        if (offsetRight > ruler) {
          container.style.left = reference.offsetWidth - offsetRight + 'px'
        }
      },
      /**
       * Moves the indicator bar to the left on click to show currently invisible indicators.
       * @param {Element} element The clicked indicator element
       * @param {Element} container The container holding clicked element and to be moved
       * @param {Element} reference The reference container in which the container element should be moved
       */
      rulerLeftHandler (element, container, reference) {
        if (Math.abs(container.offsetLeft) > element.offsetLeft) {
          container.style.left = 0 - element.offsetLeft + 'px'
        }
      },
      /**
       * Moved the container holding the slides depending on slide direction.
       */
      moveContentWrapper () {
        if (!this.slides.length) {
          return
        }

        switch (this.animationType) {
          case ANIMATION_TYPE.toptobottom: {
            this.slideTrack.style = `top: -${this.current * this.slides[0].offsetHeight}px;`
            break
          }
          case ANIMATION_TYPE.bottomtotop: {
            this.slideTrack.style = `top: ${this.current * this.slides[0].offsetHeight}px;`
            break
          }
          case ANIMATION_TYPE.lefttoright: {
            this.slideTrack.style = `left: -${this.current * this.slides[0].offsetWidth}px;`
            break
          }
          case ANIMATION_TYPE.righttoleft: {
            this.slideTrack.style = `left: ${this.current * this.slides[0].offsetWidth}px;`
            break
          }
          default: {
          }
        }
      },
      /**
       * Starts auto sliding.
       */
      startSlide () {
        this.autoslide = setInterval(this.goNext, +this.interval)
      }
    },
    mounted () {
      this.$nextTick(() => {
        document.addEventListener('gridGapChanged', this.moveContentWrapper)
        document.addEventListener('colCountChanged', this.moveContentWrapper)
      })

      this.$nextTick().then(() => {
        this.slideTrack = this.$el.querySelector('[data-wrapper] > div')
        this.slides = this.$el.querySelectorAll('[data-wrapper] > div > [data-component]')
        this.indicators = this.$el.querySelectorAll('[data-part="indicators"] [data-indicator]')

        if (this.slides.item(this.current)) {
          this.slides.item(this.current).toggleAttribute('data-current', true)
        }

        if (!this.indicators.length) {
          // via timeout, because IE11 has no indicators rendered in the promise
          setTimeout(() => {
            this.indicators = this.$el.querySelectorAll('[data-part="indicators"] [data-indicator]')
            if (this.indicators.item(this.current)) {
              this.indicators.item(this.current).toggleAttribute('data-current', true)
            }
          }, 200)
        }

        if (+this.interval) {
          this.startSlide()
          this.$el.addEventListener('mouseover', () => {
            clearInterval(this.autoslide)
          })
          this.$el.addEventListener('mouseout', () => {
            this.startSlide()
          })
        }
      })
    },
    watch: {
      current: function () {
        const target = this.indicators.item(this.current)
        // Make movement of the indicators
        const elIndicators = (target && target.parentNode) || null
        if (!elIndicators) {
          return elIndicators
        }
        // Check whether target is in visual zone
        this.rulerRightHandler(target, elIndicators, this.$el)
        this.rulerLeftHandler(target, elIndicators, this.$el)
        // If target has next sibling, check whether target.nextSibling is in visual zone
        target.nextSibling && target.offsetWidth + target.nextSibling.offsetWidth < this.$el.offsetWidth && this.rulerRightHandler(target.nextSibling, elIndicators, this.$el)
        // If target has previous sibling, check whether target.nextSibling is in visual zone
        target.previousSibling && target.offsetWidth + target.previousSibling.offsetWidth < this.$el.offsetWidth && this.rulerLeftHandler(target.previousSibling, elIndicators, this.$el)

        this.slides.forEach((item, index) => item.toggleAttribute('data-current', index === this.current))
        this.indicators.forEach((item, index) => item.toggleAttribute('data-current', index === this.current))
      }
    },
    beforeDestroy () {
      clearInterval(this.autoslide)
    }
  }
</script>
